<script>
import {defineComponent, watch, ref, onMounted, onUnmounted} from 'vue'
import {useCartStore} from "@js/store/CartStore.js";
import CustomCheckbox from "@js/components/Input/CustomCheckbox.vue";
import IconCheckMark from "@js/components/Icons/IconCheckMark.vue";
import {getAddressSuggestions} from "@js/yandex-api.ts";
import {checkDocument} from "@js/helpers.ts";

export default defineComponent({
    name: "CartDelivery",
    components: {IconCheckMark, CustomCheckbox},
    setup() {
        const cartStore = useCartStore();
        const query = ref(cartStore.form.delivery_address);
        const suggestions = ref([]);
        const dropdownRef = ref(null); // ссылка на блок с инпутом и выпадашкой

        let debounce = null;

        const fetchSuggestions = () => {
            clearTimeout(debounce);
            debounce = setTimeout(async () => {
                if (query.value.length < 3) {
                    suggestions.value = [];
                    return;
                }
                suggestions.value = await getAddressSuggestions(query.value);
            }, 300);
        };

        const selectSuggestion = (value) => {
            query.value = value;
            cartStore.form.delivery_address = value;
            suggestions.value = [];
        };

        watch(query, (val) => {
            cartStore.form.delivery_address = val;
        });

        const handleClickOutside = (event) => {
            if (!dropdownRef.value?.contains(event.target)) {
                suggestions.value = [];
            }
        };

        onMounted(() => {
            if (!checkDocument()) {
                document.addEventListener('click', handleClickOutside);
            }
        });

        onUnmounted(() => {
            if (!checkDocument()) {
                document.removeEventListener('click', handleClickOutside);
            }
        });

        return {
            cartStore,
            query,
            suggestions,
            fetchSuggestions,
            selectSuggestion,
            dropdownRef,
        };
    }
})
</script>

<template>
    <div class="bg-grayBg p-5 md:p-8 rounded-2xl space-y-6">
        <div>
            <h2 class="text-xl font-medium mb-3">Выберите способ доставки</h2>

            <div class="space-y-4">
                <label class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                       :class="[
                                cartStore.form.delivery_type === 'pickup'
                                ? 'border-baseColor border-solid'
                                : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
                            ]">
                    <input type="radio" v-model="cartStore.form.delivery_type" value="pickup"
                           class="mt-1 checked:border-baseColor checked:bg-baseColor max-md:active:border-baseColor focus:ring-baseColor"/>
                    <div>
                        <div class="font-medium">Самовывоз</div>
                        <div class="text-sm text-gray-500">(получение в пункте выдачи – бесплатно)</div>
                    </div>
                </label>
                <label class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                       :class="[
                                cartStore.form.delivery_type === 'inside_mkad'
                                ? 'border-baseColor border-solid'
                                : 'border-gray-500 md:border-dashed md:hover:border-baseColor max-md:active:border-baseColor md:hover:border-solid'
                            ]">
                    <input type="radio" v-model="cartStore.form.delivery_type" value="inside_mkad"
                           class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <div class="flex justify-between w-full">
                        <div>
                            <div class="font-medium">Доставка в пределах МКАД</div>
                            <div class="text-sm text-gray-500">(при заказе от 50 000 руб. – бесплатно)</div>
                        </div>
                        <div class="font-semibold whitespace-nowrap">2 500 руб.</div>
                    </div>
                </label>
                <label class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                       :class="[
                                cartStore.form.delivery_type === 'outside_mkad'
                                ? 'border-baseColor border-solid'
                                : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
                            ]">
                    <input type="radio" v-model="cartStore.form.delivery_type" value="outside_mkad"
                           class="mt-1 checked:border-baseColor checked:bg-baseColor max-md:active:border-baseColor focus:ring-baseColor"/>
                    <div class="flex flex-col w-full gap-2">
                        <div class="flex justify-between">
                            <div>
                                <div class="font-medium">Доставка за пределами МКАД</div>
                                <div class="text-sm text-gray-500">(цена за 1 км.)</div>
                            </div>
                            <div class="font-semibold whitespace-nowrap">
                                {{ cartStore.form.delivery_distance * 70 }} руб.
                            </div>
                        </div>
                        <div class="text-sm text-gray-500">
                            Укажите расстояние от МКАД:
                            <input type="number" min="0" v-model.number="cartStore.form.delivery_distance"
                                   class="ml-2 px-2 py-1 w-20 border border-gray-300 rounded"/>
                            км.
                        </div>
                    </div>
                </label>
                <label class="bg-white p-3 rounded border transition flex items-start gap-3 cursor-pointer"
                       :class="[
                                cartStore.form.delivery_type === 'russia'
                                ? 'border-baseColor border-solid'
                                : 'border-gray-500 md:border-dashed md:hover:border-baseColor md:hover:border-solid'
                            ]">
                    <input type="radio" v-model="cartStore.form.delivery_type" value="russia"
                           class="mt-1 checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <div>
                        <div class="font-medium">Доставка по России</div>
                        <div class="text-sm text-gray-500">Срок и стоимость доставки определяются после оформления
                            заказа, ожидайте нашего звонка
                        </div>
                    </div>
                </label>
                <div>
                    <div v-if="cartStore.form.delivery_type !== 'pickup'" ref="dropdownRef" class="relative mb-4">
                        <input v-model="query" @input="fetchSuggestions" type="text"
                               placeholder="Адрес доставки" autocomplete="off"
                               class="w-full mt-3 px-4 py-2 mb-1 border border-gray-300 focus:border-baseColor focus:ring-baseColor rounded-md"/>
                        <ul v-if="suggestions.length"
                            class="absolute z-10 w-full bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto">
                            <li v-for="(suggestion, i) in suggestions" :key="i"
                                @click="selectSuggestion(suggestion)"
                                class="px-4 py-2 md:hover:bg-gray-100 cursor-pointer text-sm">
                                {{ suggestion }}
                            </li>
                        </ul>
                    </div>
                    <label class="flex items-center relative" for="need_lift_checkbox">
                        <input id="need_lift_checkbox" type="checkbox"
                               v-model="cartStore.form.need_lift"
                               class="peer hidden"/>
                        <div
                            class="w-4 h-4 border bg-grayBg border-gray-300 rounded-sm cursor-pointer relative flex items-center justify-center text-black text-xs font-bold transition-colors duration-300 peer-checked:bg-baseColor peer-checked:border-baseColor">
                            <transition
                                enter-active-class="transition duration-150 ease-out transform"
                                enter-from-class="translate-y-2 opacity-0"
                                enter-to-class="translate-y-0 opacity-50"
                                leave-active-class="transition duration-100 ease-in transform"
                                leave-from-class="translate-y-0 opacity-50"
                                leave-to-class="translate-y-2 opacity-0">
                                <IconCheckMark v-if="cartStore.form.need_lift"
                                               class="w-3 h-3 text-black"/>
                            </transition>
                        </div>
                        <span class="ms-2 text-sm font-medium">
                           "Подъём товара на этаж". (Данная услуга является платной. Стоимость рассчитывается менеджером.)
                       </span>
                    </label>
                </div>
            </div>
        </div>
        <div>
            <h2 class="text-xl font-medium mb-3">Выберите способ оплаты</h2>
            <div class="space-y-3">
                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input type="radio" v-model="cartStore.form.payment_type" value="cash"
                           class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <span>Наличные</span>
                </label>
                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input type="radio" v-model="cartStore.form.payment_type" value="card"
                           class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <span>Банковской картой</span>
                </label>
                <label class="flex items-center gap-3 cursor-pointer font-medium">
                    <input type="radio" v-model="cartStore.form.payment_type" value="invoice"
                           class="checked:border-baseColor checked:bg-baseColor focus:ring-baseColor"/>
                    <span>Счёт для юр.лиц</span>
                </label>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
input[type="radio"],
input[type="checkbox"] {
    accent-color: #ef5b27;
}
</style>
